import { withStyles } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import PeopleIcon from '@material-ui/icons/People';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import DirectionsBikeIcon from '@material-ui/icons/DirectionsBike';
import TerrainIcon from '@material-ui/icons/Terrain';
import * as Sentry from '@sentry/browser';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
  BooleanField,
  Button,
  Create,
  DateField,
  DeleteButton,
  Edit,
  EmailField,
  Filter,
  FormDataConsumer,
  FunctionField,
  FormInput,
  FormTab,
  Labeled,
  LinearProgress,
  List,
  LongTextInput,
  NumberField,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  ReferenceManyField,
  SaveButton,
  Show,
  SimpleForm,
  Tab,
  TabbedForm,
  TabbedShowLayout,
  TextField,
  TextInput,
  Toolbar,
  linkToRecord,
  required,
} from 'react-admin';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { crudCreate } from '../actions';
import { clearRouteBooks, generateRouteBooks, generateTagsDocument } from '../api';
import { QUERY_PER_PAGE } from '../constants';
import { withLoading } from '../sagas';
import { downloadURI } from '../utils';
import AddNewButton from './button/AddNewButton';
import DownloadButton from './button/DownloadButton';
import ShowButton from './button/ShowButton';
import Query from './data/Query';
import Datagrid from './detail/Datagrid';
import EditActions from './detail/EditActions';
import ShowActions from './detail/ShowActions';
import ConditionalField from './fields/ConditionalField';
import LongTextField from './fields/LongTextField';
import ReferenceManyFieldLoadingWrapper from './fields/ReferenceManyFieldLoadingWrapper';
import AutocompleteInput from './inputs/AutocompleteInput';
import DateInput from './inputs/DateInput';
import SelectInput from './inputs/SelectInput';
import MultiLangBlock from './other/MultiLangBlock';

const toolbarStyles = {
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
  },
};

const durationValidator = value => {
  if (value <= 0 || !Number.isInteger(value)) {
    return 'Please insert a valid number of days.';
  }
};

const lengthValidator = maxLength => value => {
  if (value && value.length > maxLength) {
    return `Must be at most ${maxLength} characters.`;
  }
};

const tourCodeValidator = (value, allValues) => {
  const activityType = allValues.tourActivityType;
  if (!value || !activityType) return undefined;

  const hasH = /\d+H\d+/.test(value);

  if (activityType === 'hike' && !hasH) {
    return 'Activity Type is "Hike" - Tour code must contain H (e.g., 1H2)';
  }

  if (activityType === 'bike' && hasH) {
    return 'Activity Type is "Bike" - Tour code must not contain H';
  }

  return undefined;
};

const TITLE_SINGULAR = 'Tour';
const TITLE_PLURAL = 'Tours';

const ACTIVITY_TYPE_CHOICES = [
  { id: 'bike', name: 'Bike', icon: DirectionsBikeIcon },
  { id: 'hike', name: 'Hike', icon: TerrainIcon },
];

const ActivityTypeOptionRenderer = choice => (
  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    {choice.icon && React.createElement(choice.icon, { style: { fontSize: '1.2rem' } })}
    {choice.name}
  </span>
);

const TourTitle = ({ record }) => (
  <span>
    {TITLE_SINGULAR} "{record.name}"
  </span>
);

const ActivityTypeField = ({ record }) => {
  const isBike = record.tourActivityType === 'bike';
  return (
    <span style={{ display: 'flex', alignItems: 'center' }} title={isBike ? 'Bike' : 'Hike'}>
      {isBike ? <DirectionsBikeIcon /> : <TerrainIcon />}
    </span>
  );
};
ActivityTypeField.propTypes = { record: PropTypes.shape({ tourActivityType: PropTypes.string }) };

const BikeField = ({ record }) => {
  if (!record.bikeId) return '-';

  return (
    <Query type="GET_ONE" resource="bike" payload={{ id: record.bikeId }}>
      {({ data, loading }) => {
        if (loading) return '...';
        if (!data) return '-';
        return (
          <Query type="GET_ONE" resource="bikeModel" payload={{ id: data.modelId }}>
            {({ data: model, loading: modelLoading }) => {
              if (modelLoading) return '...';
              if (!model) return `${data.code} - - - ${data.size}`;
              return `${data.code} - ${model.name} - ${data.size}`;
            }}
          </Query>
        );
      }}
    </Query>
  );
};
BikeField.propTypes = { record: PropTypes.shape({ bikeId: PropTypes.number }) };
TourTitle.propTypes = { record: PropTypes.shape({ name: PropTypes.string }) };

const TourFilters = props => (
  <Filter {...props}>
    <TextInput label="Search" source="q__name_tourCode" alwaysOn />
    <SelectInput
      label="Activity"
      source="tourActivityType"
      choices={ACTIVITY_TYPE_CHOICES}
      optionText={ActivityTypeOptionRenderer}
      alwaysOn
    />
  </Filter>
);

export const TourList = props => (
  <List
    title={TITLE_PLURAL}
    bulkActions={false}
    sort={{ field: 'id', order: 'DESC' }}
    filters={<TourFilters />}
    {...props}
  >
    <Datagrid rowClick="edit">
      <FunctionField label="Activity" render={record => <ActivityTypeField record={record} />} />
      <TextField source="tourCode" />
      <TextField source="name" />
      <ReferenceField source="regionId" reference="region">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField source="tourTypeId" reference="tourType">
        <TextField source="name" />
      </ReferenceField>
      <DateField source="startDate" locales="pt-PT" />
      <ShowButton />
    </Datagrid>
  </List>
);

const tourShowStyles = {
  participantsActionsBar: {
    paddingTop: '16px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  participantsAction: {
    marginLeft: '16px',
  },
};

export const HotelsAssignedField = props => (
  <Query
    type="GET_MANY_REFERENCE"
    resource="participantToTourDayLink"
    payload={{
      target: 'tourDayId',
      id: props.record.id,
      pagination: { perPage: 100 },
      sort: { field: 'id', order: 'asc' },
    }}
  >
    {({ data, loading, error }) => {
      if (loading) {
        return <LinearProgress />;
      }
      if (error) {
        return (
          <Typography color="error" gutterBottom>
            Failed to fetch hotels data.
          </Typography>
        );
      }
      const totalHotels = data.length;
      const hotelsAssigned = data.filter(link => link.hotelId !== null).length;
      if (hotelsAssigned === totalHotels) {
        return <Typography variant="body1">All</Typography>;
      } else if (hotelsAssigned === 0) {
        return <Typography variant="body1">None</Typography>;
      } else {
        return <Typography variant="body1">Some</Typography>;
      }
    }}
  </Query>
);

HotelsAssignedField.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
};

const editWithRedirect = (location, history, id, basePath) =>
  history.push({
    pathname: linkToRecord(basePath, id),
    state: {
      redirect: location.pathname,
    },
  });

const TourDayTabContent = withRouter(({ location, history, edit, ...restProps }) => (
  <ReferenceManyField
    reference="tourDay"
    target="tourId"
    sort={{ field: 'number', order: 'ASC' }}
    addLabel={false}
    {...restProps}
  >
    <ReferenceManyFieldLoadingWrapper loadingText="Tour has no days.">
      <Datagrid rowClick={(id, basePath) => editWithRedirect(location, history, id, basePath)}>
        <NumberField source="number" />
        <ReferenceField source="routeId" reference="route" allowEmpty>
          <TextField source="internalName" />
        </ReferenceField>
        <ReferenceField source="transferId" reference="transfer" allowEmpty>
          <TextField source="companyName" />
        </ReferenceField>
        <HotelsAssignedField label="Hotels assigned" />
        <BooleanField source="hasBreakfast" />
        <BooleanField source="hasLunch" />
        <BooleanField source="hasDinner" />
        <ShowButton />
      </Datagrid>
    </ReferenceManyFieldLoadingWrapper>
  </ReferenceManyField>
));

const ParticipantTabContent = withRouter(
  connect(null, dispatch => ({
    syncTourParticipants: tourId =>
      dispatch({ type: 'SYNC_TOUR_PARTICIPANTS', payload: { tourId } }),
  }))(props => {
    const [loadingRouteBooks, setLoadingRouteBooks] = useState(false);
    const { syncTourParticipants, edit, location, history, ...restProps } = props;
    const tourId = parseInt(props.tourId);

    const handleClearRouteBooks = withLoading({ refresh: true })(async () => {
      setLoadingRouteBooks(true);
      try {
        await clearRouteBooks({ tourId });
      } finally {
        setLoadingRouteBooks(false);
      }
    });

    const handleGenerateRouteBooks = withLoading({ refresh: true })(async () => {
      setLoadingRouteBooks(true);
      try {
        await generateRouteBooks({ tourId, forMissingOnly: true });
      } finally {
        setLoadingRouteBooks(false);
      }
    });

    return (
      <>
        {edit && (
          <div style={tourShowStyles.participantsActionsBar}>
            <Button
              label="Download Tags"
              variant="contained"
              onClick={withLoading()(async () => {
                try {
                  const { data: tagsDocumentUrl } = await generateTagsDocument({
                    tourId,
                  });
                  downloadURI(tagsDocumentUrl, `tags-tour-${props.tourId}.pdf`);
                } catch (err) {
                  console.error(err);
                  Sentry.captureException(err);
                }
              })}
              style={tourShowStyles.participantsAction}
            >
              <PictureAsPdfIcon />
            </Button>
            {props.record.tourCode && (
              <Button
                label="Sync Participants"
                variant="contained"
                onClick={() => syncTourParticipants(tourId)}
                style={tourShowStyles.participantsAction}
              >
                <PeopleIcon />
              </Button>
            )}
            <Button
              label="Clear All Route Books"
              variant="contained"
              onClick={handleClearRouteBooks}
              style={tourShowStyles.participantsAction}
            >
              <PictureAsPdfIcon />
            </Button>
            <Button
              label="Generate Missing Route Books (max 5)"
              variant="contained"
              onClick={handleGenerateRouteBooks}
              style={tourShowStyles.participantsAction}
            >
              <PictureAsPdfIcon />
            </Button>
          </div>
        )}
        <ReferenceManyField reference="participant" target="tourId" addLabel={false} {...restProps}>
          <ReferenceManyFieldLoadingWrapper loadingText="Tour has no participants.">
            <Datagrid
              rowClick={(id, basePath) => editWithRedirect(location, history, id, basePath)}
            >
              <TextField source="name" />
              <EmailField source="email" />
              <TextField source="accessCode" />
              <FunctionField label="Bike" render={record => <BikeField record={record} />} />
              <TextField source="pedalSystem" label="Pedals" />
              <ReferenceField source="languageId" reference="language" label="Language">
                <TextField source="code" />
              </ReferenceField>
              {loadingRouteBooks ? (
                <>
                  <CircularProgress size={20} />
                </>
              ) : (
                <ConditionalField showIf="routeBookUrl" label="Route Book">
                  <DownloadButton source="routeBookUrl" />
                </ConditionalField>
              )}
              <ShowButton />
            </Datagrid>
          </ReferenceManyFieldLoadingWrapper>
        </ReferenceManyField>
        {edit && (
          <AddNewButton
            label="Add new participant"
            createPath="/participant/create"
            parentIdField="tourId"
            redirect={location.pathname}
            {...restProps}
          />
        )}
      </>
    );
  }),
);
ParticipantTabContent.propTypes = {
  tourId: PropTypes.string.isRequired,
  edit: PropTypes.bool,
};
ParticipantTabContent.displayName = 'ParticipantTabContent';

export const TourShow = props => {
  return (
    <Show title={<TourTitle />} actions={<ShowActions />} {...props}>
      <TabbedShowLayout>
        <Tab label="Main">
          <TextField source="name" />
          <TextField source="tourCode" />
          <ReferenceField source="regionId" reference="region">
            <TextField source="name" />
          </ReferenceField>
          <DateField source="startDate" locales="pt-PT" />
          <TextField source="tourActivityType" label="Activity Type" />
          <ReferenceField source="tourTypeId" reference="tourType">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField source="guide1Id" reference="guide" allowEmpty>
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField source="guide2Id" reference="guide" allowEmpty>
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField source="guide3Id" reference="guide" allowEmpty label="Guide3 (View Only)">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField source="guide4Id" reference="guide" allowEmpty label="Guide4 (View Only)">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField source="guide5Id" reference="guide" allowEmpty label="Guide5 (View Only)">
            <TextField source="name" />
          </ReferenceField>
          <LongTextField source="guideNotes" />
          <MultiLangBlock isShow>
            <TextField source="terrainType" label="Terrain type" />
          </MultiLangBlock>
          <TextField source="whatsAppGroupUrl" />
        </Tab>
        <Tab label="Days" path="days">
          <TourDayTabContent tourId={props.id} />
        </Tab>
        <Tab label="Participants" path="participants">
          <ParticipantTabContent tourId={props.id} />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};
TourShow.propTypes = {
  id: PropTypes.string.isRequired,
  location: PropTypes.object.isRequired,
};

const TourEditToolbar = withRouter(
  withStyles(toolbarStyles)(({ location, match, staticContext, ...restProps }) => {
    return location.pathname === match.url ? (
      <Toolbar {...restProps}>
        <SaveButton undoable={false} />
        <DeleteButton undoable={false} />
      </Toolbar>
    ) : null;
  }),
);
TourEditToolbar.displayName = 'TourEditToolbar';

export const TourEdit = props => (
  <Edit title={<TourTitle />} actions={<EditActions />} {...props}>
    <TabbedForm toolbar={<TourEditToolbar />}>
      <FormTab label="Main">
        <SelectInput
          source="tourActivityType"
          label="Activity Type"
          choices={ACTIVITY_TYPE_CHOICES}
          optionText={ActivityTypeOptionRenderer}
          validate={[required()]}
        />
        <TextInput source="name" validate={[required()]} />
        <TextInput source="tourCode" validate={[lengthValidator(10), required(), tourCodeValidator]} autoFocus />
        <ReferenceInput
          perPage={QUERY_PER_PAGE}
          source="regionId"
          reference="region"
          validate={[required()]}
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
        <DateInput source="startDate" validate={[required()]} />
        <ReferenceInput
          perPage={QUERY_PER_PAGE}
          source="tourTypeId"
          reference="tourType"
          validate={[required()]}
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput perPage={QUERY_PER_PAGE} source="guide1Id" reference="guide" resettable>
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput perPage={QUERY_PER_PAGE} source="guide2Id" reference="guide" resettable>
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          perPage={QUERY_PER_PAGE}
          source="guide3Id"
          reference="guide"
          resettable
          label="Guide3 (View Only)"
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          perPage={QUERY_PER_PAGE}
          source="guide4Id"
          reference="guide"
          resettable
          label="Guide4 (View Only)"
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          perPage={QUERY_PER_PAGE}
          source="guide5Id"
          reference="guide"
          resettable
          label="Guide5 (View Only)"
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
        <LongTextInput source="guideNotes" />
        <MultiLangBlock>
          <TextInput source="terrainType" label="Terrain type" validate={[required()]} />
        </MultiLangBlock>
        <TextInput source="whatsAppGroupUrl" />
      </FormTab>
      <FormTab label="Days" path="days">
        <TourDayTabContent tourId={props.id} edit />
      </FormTab>
      <FormTab label="Participants" path="participants">
        <ParticipantTabContent tourId={props.id} edit />
      </FormTab>
    </TabbedForm>
  </Edit>
);
TourEdit.propTypes = {
  id: PropTypes.string.isRequired,
  location: PropTypes.object.isRequired,
};

/**
 * Select component to select unused tour group ids.
 */
const UnusedTourGroupIdsSelect = () => (
  <FormDataConsumer>
    {({ formData }) => {
      const activityType = formData && formData.tourActivityType;

      console.log('UnusedTourGroupIdsSelect - formData:', formData);
      console.log('UnusedTourGroupIdsSelect - activityType:', activityType);

      return (
        <Query type="CUSTOM" resource="fetchUnusedTourGroupIds">
          {({ data, loading, error }) => {
            if (loading) {
              return (
                <Labeled label="Unused Tour Codes" source="siteGroupId">
                  <LinearProgress />
                </Labeled>
              );
            }
            if (error) {
              return (
                <Typography color="error" gutterBottom>
                  Failed to fetch unused tour codes.
                </Typography>
              );
            }

            console.log('UnusedTourGroupIdsSelect - raw data:', data);

            // Filter codes based on activity type
            let filteredData = data;
            if (activityType) {
              const hasH = /\d+H\d+/;
              filteredData = data.filter(code => {
                const codeHasH = hasH.test(code);
                return activityType === 'hike' ? codeHasH : !codeHasH;
              });
              console.log('UnusedTourGroupIdsSelect - filtered data:', filteredData);
            }

            return (
              <FormInput
                input={
                  <AutocompleteInput
                    label="Unused Tour Codes"
                    source="siteGroupId"
                    allowEmpty
                    choices={filteredData.sort().map(siteGroupId => ({ id: siteGroupId }))}
                    optionText="id"
                    // shouldRenderSuggestions={val => {
                    //   return val.trim().length > 2;
                    // }}
                  />
                }
              />
            );
          }}
        </Query>
      );
    }}
  </FormDataConsumer>
);

const TourCreateToolbar = connect()(props => {
  const { dispatch, ...restProps } = props;
  const { resource, basePath, handleSubmit, redirect } = restProps;
  const handleSave = () => {
    return handleSubmit(values => {
      const { duration, ...createValues } = values;
      dispatch(
        crudCreate(resource, createValues, basePath, redirect, {
          onSuccess: ({ payload: { data } }) =>
            dispatch({ type: 'TOUR_CREATED', payload: { data: { ...data, duration } } }),
        }),
      );
    });
  };
  return (
    <Toolbar {...restProps}>
      <SaveButton handleSubmitWithRedirect={handleSave} />
    </Toolbar>
  );
});
TourCreateToolbar.propTypes = {
  resource: PropTypes.string,
  basePath: PropTypes.string,
  handleSubmit: PropTypes.func,
  redirect: PropTypes.string,
};
TourCreateToolbar.displayName = 'TourCreateToolbar';

export const TourCreate = props => {
  return (
    <Create {...props}>
      <SimpleForm toolbar={<TourCreateToolbar />}>
        <SelectInput
          source="tourActivityType"
          label="Activity Type"
          choices={ACTIVITY_TYPE_CHOICES}
          optionText={ActivityTypeOptionRenderer}
          defaultValue="bike"
          validate={[required()]}
        />
        <UnusedTourGroupIdsSelect />
        <TextInput source="name" validate={[required()]} autoFocus />
        <TextInput source="tourCode" validate={[lengthValidator(10), required(), tourCodeValidator]} />
        <ReferenceInput
          perPage={QUERY_PER_PAGE}
          source="regionId"
          reference="region"
          validate={[required()]}
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
        <DateInput source="startDate" validate={[required()]} />
        <NumberInput source="duration" validate={[durationValidator, required()]} />
        <ReferenceInput
          perPage={QUERY_PER_PAGE}
          source="tourTypeId"
          reference="tourType"
          validate={[required()]}
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
        <MultiLangBlock>
          <TextInput source="terrainType" label="Terrain type" validate={[required()]} />
        </MultiLangBlock>
        <ReferenceInput perPage={QUERY_PER_PAGE} source="guide1Id" reference="guide" resettable>
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput perPage={QUERY_PER_PAGE} source="guide2Id" reference="guide" resettable>
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          perPage={QUERY_PER_PAGE}
          source="guide3Id"
          reference="guide"
          resettable
          label="Guide3 (View Only)"
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          perPage={QUERY_PER_PAGE}
          source="guide4Id"
          reference="guide"
          resettable
          label="Guide4 (View Only)"
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          perPage={QUERY_PER_PAGE}
          source="guide5Id"
          reference="guide"
          resettable
          label="Guide5 (View Only)"
        >
          <SelectInput optionText="name" />
        </ReferenceInput>

        <LongTextInput source="guideNotes" />
        <TextInput source="whatsAppGroupUrl" />
      </SimpleForm>
    </Create>
  );
};
